import React, { useEffect, useState, Suspense } from 'react';
import { Check, X, MessageSquare, Brain, Mic, Loader2 } from 'lucide-react';
import { api } from '@/lib/supabase';
import SeoHead from '../seo/SeoHead';


const Pricing = () => {
    const [currentPlan, setCurrentPlan] = useState('')
    const [loading, setLoading] = useState(true);

    const plans = [
        {
            title: 'Free',
            price: 'Free',
            buttonVariant: 'secondary' as const,
            price_id: null, // No payment needed
            features: [
                { text: '20 text messages per day', included: true },
                { text: 'One-time personality test', included: true },
                { text: '3 goals limit', included: true },
                { text: '5 journal entries/month', included: true },
                { text: 'No voice chat', included: false },
            ],
        },
        {
            title: 'Premium',
            price: '$29',
            price_id: 'price_1TyX9qI1bE53WyXKppNqCiqi', // Clarana Premium monthly
            buttonVariant: 'primary' as const,
            isPopular: true,
            features: [
                { text: 'Unlimited text messages', included: true },
                { text: 'Personality test (unlimited)', included: true },
                { text: '60 min voice chat/month', included: true },
                { text: 'Unlimited goals & journal', included: true },
            ],
        },
        {
            title: 'Professional',
            price: '$89',
            price_id: 'price_1TyXAwI1bE53WyXKuPnM9oYF', // Clarana Professional monthly
            buttonVariant: 'secondary' as const,
            features: [
                { text: 'Unlimited text messages', included: true },
                { text: 'Personality test (unlimited)', included: true },
                { text: '180 min voice chat/month', included: true },
                { text: 'Unlimited goals & journal', included: true },
            ],
        },
    ];

    const loadPaymentLink = async (pricing_id: string) => {
        try {
            const data: any = await api.createSubscriptionPaymentLink(pricing_id)
            window.location.replace(data)
        } catch (error) {
            console.error('Failed to load pricing:', error);
        }
    };

    const getSubcription = async () => {
        try {

            const data: any = await api.updateSubscriptionFromStripe()
            if (!data) {
                const subscriptionData: any = await api.getSubscription()
                console.log('subscriptionData?.is_premium', subscriptionData)
                if (!subscriptionData?.data?.is_premium) {
                    setCurrentPlan('Free')
                }
                console.log('subscriptionData', subscriptionData?.data)
                setLoading(false);
                return;
            }
            else {
                const subscriptionData: any = await api.getSubscription()
                console.log('subscriptionData?.is_premium', subscriptionData?.is_premium)
                if (!subscriptionData?.data?.is_premium) {
                    setCurrentPlan('Free')
                }
                setLoading(false);
                return;
            }
        } catch (err) {
            const subscriptionData: any = await api.getSubscription()
            console.log('subscriptionData', subscriptionData?.data)
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getSubcription();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
                <div className="flex items-center space-x-2 text-white">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span>Loading pricing...</span>
                </div>
            </div>
        );
    }

    const buttonStyles: any = {
        default: 'bg-gray-200 hover:bg-gray-300 text-gray-800',
        primary: 'bg-indigo-600 hover:bg-indigo-700 text-white',
        secondary: 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-300'
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
            <SeoHead
                title="Pricing — Clarana"
                description="Compare Clarana Free, Premium, and Professional plans. Choose the AI companion tier that fits your growth journey."
                path="/pricing"
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-white sm:text-5xl">
                        Choose Your <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">Plan</span>
                    </h1>
                    <p className="mt-5 text-xl text-gray-300">
                        Select the perfect plan for your needs
                    </p>
                </div>

                <div className="mt-16">
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                        {plans.map((plan: any, index) => (
                            <div 
                                key={index}
                                className={`relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border ${
                                    plan?.isPopular 
                                        ? 'border-violet-500/50 bg-gradient-to-br from-violet-500/10 to-purple-500/10' 
                                        : 'border-white/10'
                                }`}
                            >
                                {plan?.isPopular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                        <span className="bg-gradient-to-r from-violet-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                                            Most Popular
                                        </span>
                                    </div>
                                )}
                                <div className="text-center">
                                    <h3 className="text-2xl font-bold text-white">{plan?.title}</h3>
                                    <div className="mt-4 flex items-baseline justify-center">
                                        <span className="text-5xl font-bold tracking-tight text-white">{plan?.price}</span>
                                        {plan?.price !== 'Free' && <span className="text-lg text-gray-400">/month</span>}
                                    </div>
                                </div>
                                <ul className="mt-8 space-y-4">
                                    {plan?.features.map((feature: any, featureIndex: number) => (
                                        <li key={featureIndex} className="flex items-start">
                                            <div className="flex-shrink-0">
                                                {feature.included ? (
                                                    <Check className="h-6 w-6 text-violet-400" />
                                                ) : (
                                                    <X className="h-6 w-6 text-red-400" />
                                                )}
                                            </div>
                                            <span className="ml-3 text-base text-gray-300">{feature.text}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-8">
                                    {plan.title === 'Free' ? (
                                        <a
                                            href="/signup"
                                            className="block w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-200 bg-white/10 hover:bg-white/20 text-white border border-white/10 text-center"
                                        >
                                            {currentPlan === 'Free' ? 'Current Plan' : 'Get Started'}
                                        </a>
                                    ) : (
                                        <button
                                            className={currentPlan === plan.title 
                                                ? `w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-200 bg-white/10 text-gray-400 border border-white/10 cursor-not-allowed`
                                                : `w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-200 ${
                                                    plan?.buttonVariant === 'primary'
                                                        ? 'bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white'
                                                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                                                }`
                                            }
                                            onClick={() => plan.price_id && loadPaymentLink(plan.price_id)}
                                            disabled={currentPlan === plan.title || !plan.price_id}
                                        >
                                            {currentPlan === plan.title ? 'Current Plan' : 'Buy Plan'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3 text-center">
                    <div className="flex flex-col items-center bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                        <MessageSquare className="h-12 w-12 text-violet-400" />
                        <h3 className="mt-4 text-lg font-semibold text-white">Smart Conversations</h3>
                        <p className="mt-2 text-gray-400">
                            Engage in meaningful conversations with Clarana
                        </p>
                    </div>
                    <div className="flex flex-col items-center bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                        <Brain className="h-12 w-12 text-violet-400" />
                        <h3 className="mt-4 text-lg font-semibold text-white">Personality Analysis</h3>
                        <p className="mt-2 text-gray-400">
                            Get insights into your personality and communication style
                        </p>
                    </div>
                    <div className="flex flex-col items-center bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                        <Mic className="h-12 w-12 text-violet-400" />
                        <h3 className="mt-4 text-lg font-semibold text-white">Voice Interaction</h3>
                        <p className="mt-2 text-gray-400">
                            Connect through natural voice conversations
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Pricing;